import { StorageRootDto } from "../StorageRootDto";
import { ValueEncoder, HashSet, Dictionary, List } from "DiscoveryEngine.TS/Utils";
import { ObjSerUtils } from "DiscoveryEngine.TS/Serialization";
import { UpdateResultDto } from 'UvExplorerServer.Dtos.TS';
import { DtoBase } from "DiscoveryEngine.TS/Core/DtoBase";
import { IpScope } from "DiscoveryEngine.TS/Core";


export enum ApiPermission {
	Read, Write,
}


export class ApiAccessDto extends DtoBase {

	public Scope: string;
	public Permissions: HashSet<ApiPermission>;

	constructor() {
		super();

		this.Scope = "";
		this.Permissions = new HashSet<ApiPermission>();
	}

	public clear(): void {
		this.Scope = "";
		this.Permissions.Clear();
	}

	public get HasScope(): boolean {
		return !!this.Scope;
	}

	public get HasPermissions(): boolean {
		return (!!this.Permissions && this.Permissions.Count > 0);
	}

	public Write(obj: any, context: any): void {
		obj.api_access = {};
		obj = obj.api_access;

		super.Write(obj, context);

		if (this.HasScope)
			obj.scope = this.Scope;

		if (this.HasPermissions) {
			obj.permissions = [];
			ObjSerUtils.WriteCollection(obj.permissions, this.Permissions, (value: ApiPermission) => {
				let elem = {};
				elem["value"] = ApiPermission[value];
				return elem;
			}, context);
		}
	}

	public Read(obj: any, context: any): void {

		if (!obj.api_access)
			throw new Error();
		obj = obj.api_access;

		super.Read(obj, context);

		if (obj.scope !== undefined)
			this.Scope = obj.scope;

		if (obj.permissions !== undefined) {
			this.Permissions.Clear();
			ObjSerUtils.ReadCollection(obj.permissions, this.Permissions, (elem: any) => {
				let value: ApiPermission = ApiPermission[<string>(elem.value)]
				return value;
			}, context);
		}
	}

}


export class ApiKeyDto extends StorageRootDto {

	private readonly Default_IsActive: boolean = true;

	public UserGuid: string;
	public Name: string;
	public IsActive: boolean;
	public ClientIpScope: IpScope;
	public Access: Dictionary<string, ApiAccessDto>;
	public LastActivityTime: string;
	public Owner: string;

	constructor() {
		super();

		this.UserGuid = "";
		this.Name = "";
		this.IsActive = this.Default_IsActive;
		this.ClientIpScope = new IpScope();
		this.Access = new Dictionary<string, ApiAccessDto>();
		this.LastActivityTime = "";
		this.Owner = "";
	}

	public clear(): void {
		this.UserGuid = "";
		this.Name = "";
		this.IsActive = this.Default_IsActive;
		this.ClientIpScope.Clear();
		this.Access.Clear();
		this.LastActivityTime = "";
		this.Owner = "";
	}

	public get HasUserGuid(): boolean {
		return !!this.UserGuid;
	}

	public get HasName(): boolean {
		return !!this.Name;
	}

	public get HasClientIpScope(): boolean {
		return (!!this.ClientIpScope && !this.ClientIpScope.IsEmpty);
	}

	public get HasAccess(): boolean {
		return (!!this.Access && this.Access.Count > 0);
	}

	public get HasLastActivityTime(): boolean {
		return !!this.LastActivityTime;
	}

	public get HasOwner(): boolean {
		return !!this.Owner;
	}

	public CopyProps(obj: UpdateResultDto, isCreate: boolean = true) {
		super.CopyProps(obj, isCreate);

		if (isCreate) {
			this.UserGuid = obj.UserGuid;
		}
	}

	public Write(obj: any, context: any): void {
		obj.api_key = {};
		obj = obj.api_key;

		super.Write(obj, context);

		if (this.HasUserGuid)
			obj.user_guid = this.UserGuid;

		if (this.HasName)
			obj.name = this.Name;

		if (this.IsActive !== this.Default_IsActive)
			obj.is_active = ValueEncoder.EncodeBoolean(this.IsActive);

		if (this.HasClientIpScope) {
			context[IpScope.ObjSer_ObjectName] = "client_ip_scope";
			this.ClientIpScope.Write(obj, context);
			delete context[IpScope.ObjSer_ObjectName];
		}

		if (this.HasAccess) {
			obj.access = [];
			ObjSerUtils.WriteObjSerCollection(obj.access, this.Access.Values(), context);
		}

		if (this.LastActivityTime)
			obj.last_activity_time = this.LastActivityTime;

		if (this.HasOwner)
			obj.owner = this.Owner;
	}

	public Read(obj: any, context: any): void {

		this.clear();

		if (!obj.api_key)
			throw new Error();
		obj = obj.api_key;

		super.Read(obj, context);

		if (obj.user_guid !== undefined)
			this.UserGuid = obj.user_guid;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.is_active !== undefined)
			this.IsActive = ValueEncoder.DecodeBoolean(obj.is_active);

		if (obj.client_ip_scope !== undefined) {
			context[IpScope.ObjSer_ObjectName] = "client_ip_scope";
			this.ClientIpScope.Read(obj, context);
			delete context[IpScope.ObjSer_ObjectName];
		}

		if (obj.access !== undefined) {
			let accessList = new List<ApiAccessDto>();
			ObjSerUtils.ReadObjSerCollection(obj.access, accessList, (value: any) => {
				return new ApiAccessDto();
			}, context);
			accessList.ForEach((value: ApiAccessDto) => {
				this.Access.Add(value.Scope, value);
			});
		}

		if (obj.last_activity_time !== undefined)
			this.LastActivityTime = obj.last_activity_time;

		if (obj.owner !== undefined)
			this.Owner = obj.owner;
	}

}
